import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Search, Eye, Briefcase, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API = '/api';

export const AdminRecruitmentRequestsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [converting, setConverting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, [statusFilter]);

  const fetchRequests = async () => {
    try {
      let url = `${API}/admin/recruitment-requests`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (requestId, status) => {
    try {
      const response = await fetch(`${API}/admin/recruitment-requests/${requestId}/status?status=${status}`, {
        method: 'PUT',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success('Status updated');
        fetchRequests();
        if (selectedRequest?.request_id === requestId) {
          setSelectedRequest(prev => ({ ...prev, status }));
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const convertToJob = async (requestId) => {
    setConverting(true);
    try {
      const response = await fetch(`${API}/admin/recruitment-requests/${requestId}/convert-to-job`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Job post created! Redirecting to Jobs page...');
        setSelectedRequest(null);
        fetchRequests();
        // Navigate to jobs page after a short delay
        setTimeout(() => {
          navigate('/admin/jobs');
        }, 1500);
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to convert to job');
      }
    } catch (error) {
      toast.error('Failed to convert to job');
    } finally {
      setConverting(false);
    }
  };

  const filteredRequests = requests.filter(req => 
    req.full_name.toLowerCase().includes(search.toLowerCase()) ||
    req.company.toLowerCase().includes(search.toLowerCase()) ||
    req.position_title.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800',
      'in-progress': 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      converted: 'bg-purple-100 text-purple-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800',
    };
    return colors[urgency] || 'bg-gray-100 text-gray-800';
  };

  const statuses = ['new', 'in-progress', 'completed', 'cancelled', 'converted'];

  return (
    <div className="p-6" data-testid="admin-recruitment-requests-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#111827]">{t('admin.recruitmentRequests')}</h1>
        <p className="text-[#6B7280]">Manage recruitment requests from companies</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
              <Input
                placeholder={t('admin.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="ps-9"
                data-testid="search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => (
                  <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#33AADD] mx-auto"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="text-center py-8 text-[#6B7280]">{t('admin.noData')}</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Position</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Urgency</TableHead>
                    <TableHead>{t('admin.status')}</TableHead>
                    <TableHead>{t('admin.date')}</TableHead>
                    <TableHead className="text-end">{t('admin.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRequests.map((req, index) => (
                    <TableRow key={req.request_id} data-testid={`request-row-${index}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-[#111827]">{req.company}</p>
                          <p className="text-sm text-[#6B7280]">{req.full_name}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{req.position_title}</p>
                        <p className="text-sm text-[#6B7280]">{req.employment_type} · {req.work_model}</p>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p>{req.email}</p>
                          <p className="text-[#6B7280]">{req.country}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getUrgencyColor(req.urgency)}>{req.urgency}</Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={req.status} onValueChange={(v) => updateStatus(req.request_id, v)}>
                          <SelectTrigger className="w-32 h-8">
                            <Badge className={getStatusColor(req.status)}>{req.status}</Badge>
                          </SelectTrigger>
                          <SelectContent>
                            {statuses.map(s => (
                              <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-sm text-[#6B7280]">
                        {req.created_at ? format(new Date(req.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-end">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedRequest(req)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Request Details Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => setSelectedRequest(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Recruitment Request Details</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-[#6B7280]">Company</p>
                  <p className="font-medium">{selectedRequest.company}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Contact Person</p>
                  <p className="font-medium">{selectedRequest.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Email</p>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Phone</p>
                  <p className="font-medium">{selectedRequest.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Country</p>
                  <p className="font-medium">{selectedRequest.country}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Position</p>
                  <p className="font-medium">{selectedRequest.position_title}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Employment Type</p>
                  <p className="font-medium">{selectedRequest.employment_type}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Work Model</p>
                  <p className="font-medium">{selectedRequest.work_model}</p>
                </div>
                <div>
                  <p className="text-sm text-[#6B7280]">Location</p>
                  <p className="font-medium">{selectedRequest.location}</p>
                </div>
                {selectedRequest.salary_max && (
                  <div>
                    <p className="text-sm text-[#6B7280]">Salary Range</p>
                    <p className="font-medium">
                      {selectedRequest.currency} {selectedRequest.salary_min?.toLocaleString()} - {selectedRequest.salary_max?.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-sm text-[#6B7280] mb-2">Job Description</p>
                <p className="text-sm whitespace-pre-line bg-[#F9FAFB] p-4 rounded-lg">{selectedRequest.job_description}</p>
              </div>

              {selectedRequest.requirements && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Requirements</p>
                  <p className="text-sm whitespace-pre-line bg-[#F9FAFB] p-4 rounded-lg">{selectedRequest.requirements}</p>
                </div>
              )}

              {selectedRequest.notes && (
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Notes</p>
                  <p className="text-sm whitespace-pre-line bg-[#F9FAFB] p-4 rounded-lg">{selectedRequest.notes}</p>
                </div>
              )}

              <div className="border-t pt-4 space-y-4">
                <div>
                  <p className="text-sm text-[#6B7280] mb-2">Update Status</p>
                  <Select value={selectedRequest.status} onValueChange={(v) => updateStatus(selectedRequest.request_id, v)}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statuses.map(s => (
                        <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Convert to Job Post */}
                {selectedRequest.converted_to_job_id ? (
                  <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-green-800">
                      Already converted to Job Post
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="ms-auto"
                      onClick={() => navigate('/admin/jobs')}
                    >
                      View Jobs
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <Briefcase className="h-5 w-5 text-purple-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-purple-900">Convert to Job Post</p>
                      <p className="text-xs text-purple-700">Create a job posting from this request</p>
                    </div>
                    <Button 
                      onClick={() => convertToJob(selectedRequest.request_id)}
                      disabled={converting}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="convert-to-job-btn"
                    >
                      {converting ? (
                        <span className="flex items-center gap-2">
                          <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Converting...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Create Job Post
                          <ArrowRight className="h-4 w-4" />
                        </span>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRecruitmentRequestsPage;
